import '@chewy/kib-controls-styles/dist/styles/kib-button-new.css';
import '@chewy/kib-divider-styles/dist/kib-divider.css';
import '@chewy/kib-fields-new-styles/dist/styles/kib-field.css';
import '@chewy/kib-fields-new-styles/dist/styles/kib-select.css';
import '@chewy/kib-product-styles/dist/styles/kib-product-rating.css';

import { KibButtonNew } from '@chewy/kib-controls-react';
import KibDivider from '@chewy/kib-divider-react';
import { KibAddIcon } from '@chewy/kib-icons-react';
import { KibProductRating } from '@chewy/kib-product-react';
import { Fragment, useEffect, useRef, useState } from 'react';

import { useChewyApiContext } from '../Apollo/ApolloChewyApiProvider';
import { useReviewsContext } from '../ReviewsProvider/ReviewsProvider';

import { Review } from './Review';
import styles from './styles.module.scss';

// placeholder until sort and filter functionality is needed
// const reviewSortOptions = [
//   { value: 'NEWEST', label: 'Newest' },
//   { value: 'OLDEST', label: 'Oldest' },
//   { value: 'HIGHEST_RATING', label: 'Highest Rating' },
//   { value: 'LOWEST_RATING', label: 'Lowest Rating' },
//   { value: 'MOST_RELEVANT', label: 'Most Relevant' },
//   { value: 'FEATURED', label: 'Featured' },
//   { value: 'TOP_CONTRIBUTOR', label: 'Top Contributor' },
// ];

// const reviewFilterOptions = [
//   { value: 'UNSPECIFIED_FILTER', label: 'All Ratings' },
//   { value: 'RATING5', label: '5 Stars' },
//   { value: 'RATING4', label: '4 Stars' },
//   { value: 'RATING3', label: '3 Stars' },
//   { value: 'RATING2', label: '2 Stars' },
//   { value: 'RATING1', label: '1 Star' },
//   { value: 'POSITIVE', label: 'Positive' },
//   { value: 'NEGATIVE', label: 'Negative' },
// ];

export const Reviews = () => {
  const { averageOverallRating, getMoreReviews, hasMoreReviews, reviews, totalReviewCount } =
    useReviewsContext();
  const { products } = useChewyApiContext();

  // const handleSortChange = (
  //   event: React.ChangeEvent<HTMLInputElement> & React.ChangeEvent<HTMLSelectElement>,
  // ) => {
  //   if (!isReviewSort(event.target.value) || !isReviewFilter(ratingFilter)) return;
  //   setSortOption(event.target.value);
  //   setPreviousReviewLength(0);
  //   fetchReviews(event.target.value, ratingFilter, selectedPartNumbers);
  // };

  // const handleFilterChange = (
  //   event: React.ChangeEvent<HTMLInputElement> & React.ChangeEvent<HTMLSelectElement>,
  // ) => {
  //   if (!isReviewSort(sortOption) || !isReviewFilter(event.target.value)) return;
  //   setRatingFilter(event.target.value);
  //   setPreviousReviewLength(0);
  //   fetchReviews(sortOption, event.target.value, selectedPartNumbers);
  // };

  const handleSeeMoreReviews = () => {
    setPreviousReviewLength(reviews?.length ?? 0);
    getMoreReviews();
  };

  const reviewsSchema =
    reviews?.length && averageOverallRating && totalReviewCount
      ? {
          '@context': 'https://schema.org',
          '@type': 'Service',
          name: 'Get Real',
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: averageOverallRating,
            reviewCount: totalReviewCount,
          },
          review: reviews.map((review) => ({
            '@type': 'Review',
            author: { '@type': 'Person', name: review.submittedBy },
            reviewRating: {
              '@type': 'Rating',
              ratingValue: review?.rating,
              bestRating: 5,
              worstRating: 1,
            },
            datePublished: review?.submittedAt?.slice(0, 10),
            reviewBody: review?.reviewText,
          })),
        }
      : {};

  const [previousReviewLength, setPreviousReviewLength] = useState(reviews?.length ?? 0);
  const addReviewFocusRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // if user clicked 'see more reviews' and there are new reviews, set the focus of newest review for screen-readers
    if (reviews?.length > previousReviewLength && addReviewFocusRef?.current) {
      addReviewFocusRef.current.focus();
    }
  }, [previousReviewLength, reviews?.length]);

  return (
    <div className={styles.root}>
      <script
        data-testid="reviews-schema"
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: JSON.stringify(reviewsSchema) }}
      />

      <div className={styles.reviewsMeta}>
        {!!products?.length && !!averageOverallRating && (
          <div className={styles.reviewStatistics}>
            <span className={styles.reviewCount}>{totalReviewCount} reviews </span>
            <KibProductRating rating={averageOverallRating} className={styles.totalAverageRating} />
            <span>{averageOverallRating.toFixed(1)} average rating</span>
          </div>
        )}

        {/* <KibSelect
          className={styles.reviewsSort}
          label="Sort by"
          value={sortOption ?? 'NEWEST'}
          onChange={handleSortChange}
        >
          {reviewSortOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </KibSelect>

        <KibSelect
          className={styles.reviewsRatingFilter}
          label="Filter by rating"
          value={ratingFilter ?? ''}
          onChange={handleFilterChange}
        >
          {reviewFilterOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </KibSelect> */}
      </div>

      <KibDivider color="alternate" />

      <div className={styles.reviewsBox}>
        {!!reviews?.length &&
          reviews.map((review, index) => {
            // for screen reader users, find the newest unread review after user clicks 'see more reviews'
            const isNew = index === previousReviewLength;
            const reviewId = review?.id ?? [review?.submittedBy, review?.submittedAt].join('-');

            return (
              <Fragment key={reviewId}>
                <Review review={review} isNew={isNew} focusRef={addReviewFocusRef} />
                <KibDivider color="alternate" />
              </Fragment>
            );
          })}
        {reviews.length === 0 && (
          <div className={styles.noReviews}>
            <span>No reviews yet</span>
          </div>
        )}
      </div>

      {hasMoreReviews && (
        <div className={styles.seeMoreBox}>
          <KibButtonNew
            emphasis="secondary"
            data-testid="get-more-reviews-btn"
            icon={<KibAddIcon />}
            onClick={handleSeeMoreReviews}
            theme="action"
            size="small"
          >
            See more reviews
          </KibButtonNew>
        </div>
      )}
    </div>
  );
};
